/* eslint-disable react/prop-types */

import { colors } from '@lmc/common/constants'
import { ResidentStatus } from '@lmc/ts-common'
import { Button, Icon, IconType, PageHeader } from '@lmc/ui-kit'
import dayjs from 'dayjs'
import { find, startCase } from 'lodash'
import PropTypes from 'prop-types'
import React from 'react'
import { isBrowser, isTablet } from 'react-device-detect'
import { connect } from 'react-redux'
import Selectors from 'selectors'
import { getCarerSkills } from 'selectors/NeedsAndSkills'
import styled from 'styled-components'
import { isGroupUser } from 'utils'
import { ProfileField, ProfilePicture } from '../../../../components/generic'
import Operations from '../../../../operations'
import { history } from '../../../../store'
import { withRouter } from '../../../../withRouter'
import {
  TeamMemberProfilePermissions,
  TeamMemberProfilePersonal
} from './components'
import { isMultipleof } from './utils'

const StyledSContainer = styled.div`
  width: 100%;
  max-width: 2000px;
  margin-left: auto;
  margin-right: auto;
  padding: 0px 24px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    width: unset;
  }
`

const StyledListContainer = styled.div`
  height: 100%;
  max-width: 1434px;
  margin-left: auto;
  margin-right: auto;
`

const AssignedToWrapper = styled.div`
  margin-left: auto;
  margin-right: auto;
  white-space: pre-line;
`

const StyledHeaderContainer = styled.div`
  display: flex;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-right: auto;
  }
`

export class TeamMemberProfileScreen extends React.Component {
  state = {
    sendingInvite: false,
    gpConnectPart2Enabled: false
  }

  componentDidMount() {
    const {
      features,
      fetchCarerSkillOptions,
      fetchCarerSkills,
      fetchCarers,
      profile
    } = this.props

    if (features?.features?.skillsAndNeeds?.enabled) {
      fetchCarerSkillOptions()
      fetchCarerSkills(this.props.router.params.id)
    }
    if (!profile) {
      fetchCarers()
    }
  }

  componentDidUpdate(prevProps) {
    const { features, router, fetchCarerSkills } = this.props

    if (!prevProps.router.params || !router.params || !features) {
      return null
    }

    if (
      (!prevProps.features && features) ||
      prevProps.router.params.id !== router.params.id
    ) {
      if (features?.features?.skillsAndNeeds?.enabled) {
        fetchCarerSkills(router?.params?.id)
      }
    }
  }

  handleEdit = () => {
    const {
      router: {
        params: { id }
      }
    } = this.props
    history.push(`/organisation/team/${id}/profile/edit`)
  }

  handleEditSkills = () => {
    const {
      router: {
        params: { id }
      }
    } = this.props
    history.push(`/organisation/team/${id}/skills`)
  }

  getResidents = () => {
    const {
      residents,
      router: {
        params: { id }
      }
    } = this.props

    const assignedResidents = []

    residents
      ?.filter((resident) => [ResidentStatus.active].includes(resident.status))
      ?.forEach((resident, index) => {
        if (resident?.carers?.includes(id) || resident?.assignAll) {
          const name = `${resident.name.first} ${resident.name.last}`
          const isLastItem = residents?.length === index + 1
          assignedResidents.push(
            `${name}${
              isMultipleof(index + 1, 6) ? ',\n' : `${isLastItem ? '' : ','}`
            }`
          )
        }
      })

    return assignedResidents.join(` `)
  }

  getKeyworkers = () => {
    const {
      residents,
      router: {
        params: { id }
      }
    } = this.props

    const assignedResidents = []

    residents
      ?.filter((resident) => [ResidentStatus.active].includes(resident.status))
      ?.forEach((resident, index) => {
        if (resident?.keyworkers?.includes(id)) {
          const name = `${resident.name.first} ${resident.name.last}`
          const isLastItem = residents?.length === index + 1
          assignedResidents.push(
            `${name}${
              isMultipleof(index + 1, 6) ? ',\n' : `${isLastItem ? '' : ','}`
            }`
          )
        }
      })

    return assignedResidents.join(` `)
  }

  renderProfileFields = (profile, currentUserRole, homes) => {
    const isCurrentGroupUser = isGroupUser(currentUserRole)
    const isProfileGroupUser = isGroupUser(profile.role)

    return (
      <div
        className="lmc-card"
        style={styles.mainContainer}
        data-testid="team-member-profile-card"
      >
        <div
          style={{
            ...styles.infoContainer,
            display: 'inherit',
            textAlign: 'center'
          }}
          data-testid="team-member-profile-info"
        >
          <ProfileField
            label="Email"
            value={profile.email}
            style={{ marginTop: 18 }}
            data-testid="profile-email"
          />
          <ProfileField
            label="Role"
            value={`${
              profile.role === 'carehome-admin'
                ? 'Admin'
                : startCase(profile.role)
            }${
              profile.role === 'senior-carer' && !!profile?.permissionsTemplate
                ? ' +'
                : ''
            }`}
            data-testid="profile-role"
          />
          <ProfileField
            label="Status"
            value={profile.active ? 'Active' : 'Inactive'}
            data-testid="profile-status"
          />
          {this.props.teamMemberEnabled && (
            <>
              {!isProfileGroupUser && !isCurrentGroupUser && (
                <AssignedToWrapper>
                  <ProfileField
                    label="Assigned to"
                    value={this.getResidents() || ''}
                    data-testid="profile-assigned-to"
                  />
                </AssignedToWrapper>
              )}
              <ProfileField
                label="Profile created on"
                value={`${
                  profile?.createdOn
                    ? dayjs(profile?.createdOn).format('DD/MM/YYYY')
                    : ''
                }`}
                data-testid="profile-created-on"
              />
              {!isProfileGroupUser && (
                <ProfileField
                  label="Last seen"
                  value={`${
                    profile?.lastSeen
                      ? dayjs(profile?.lastSeen).format('HH:mm DD/MM/YYYY')
                      : ''
                  }`}
                  data-testid="profile-last-seen"
                />
              )}
              {!isProfileGroupUser && this.props.keyworkersEnabled && (
                <AssignedToWrapper>
                  <ProfileField
                    label="Keyworker for"
                    value={this.getKeyworkers() || 'Not assigned'}
                    data-testid="profile-keyworker-for"
                  />
                </AssignedToWrapper>
              )}
              {isCurrentGroupUser && !isProfileGroupUser && (
                <ProfileField
                  label="Sites"
                  value={
                    (profile?.homes?.length ? profile?.homes : [profile?.home])
                      ?.map(
                        (home) => homes.find((doc) => doc.id === home)?.name
                      )
                      .filter(Boolean) // Ensure no undefined values
                      .join(', ') || ''
                  }
                  data-testid="profile-sites"
                />
              )}
              <ProfileField
                label="Notes"
                value={`${profile?.summary || ''}`}
                data-testid="profile-notes"
              />
            </>
          )}
        </div>
      </div>
    )
  }

  resendInvite = () => {
    const { profile, resendInvite } = this.props
    this.setState({ sendingInvite: true })
    resendInvite(profile.id, () => this.setState({ sendingInvite: false }))
  }

  list = (canViewPersonalDetails) => {
    const { profile, features, role, homes } = this.props
    return (
      <StyledListContainer data-testid="team-member-profile-list">
        <ProfilePicture
          type="large"
          url={profile.picture}
          style={styles.image}
          data-testid="team-member-profile-picture"
        />
        <div data-testid="team-member-profile-fields">
          {this.renderProfileFields(profile, role, homes)}
        </div>
        <div data-testid="team-member-profile-permissions">
          <TeamMemberProfilePermissions
            features={features}
            profile={profile}
            gpConnectPart2Enabled={this.props.gpConnectPart2Enabled}
            testId="team-member-permissions"
          />
        </div>
        {canViewPersonalDetails && (
          <div data-testid="team-member-profile-personal">
            <TeamMemberProfilePersonal
              features={features}
              profile={profile}
              teamMemberEnabled={this.props.teamMemberEnabled}
              testId="team-member-personal"
            />
          </div>
        )}
      </StyledListContainer>
    )
  }

  render() {
    const { profile, hasAccess } = this.props

    const hasRolePermission = hasAccess(
      ['carehome-admin', 'group-admin', 'group-manager'],
      [
        {
          i: 'update',
          this: 'User',
          field: 'name'
        }
      ]
    )

    const canResendInvite = hasAccess(
      ['carehome-admin', 'group-admin', 'group-manager'],
      [{ i: 'create', this: 'UserInvitation' }]
    )

    if (!profile) return null
    return (
      <StyledSContainer data-testid="team-member-profile-screen">
        <PageHeader
          title={` ${profile.name.first} ${profile.name.last}`}
          onBack={
            this.props.isRosteringBetaEnabled
              ? undefined
              : () => history.push('/organisation/list')
          }
          onBackTitle="Team Members list"
          sticky
          testId="team-member-profile-header"
        >
          <StyledHeaderContainer data-testid="team-member-profile-actions">
            {isDesktop && (
              <Button
                disabled={this.state.sendingInvite}
                disabledByPermission={!canResendInvite}
                onClick={this.resendInvite}
                variant="default"
                style={styles.inviteButton}
                icon={<Icon name="MailOutlined" />}
                data-testid="resend-invite-button"
              >
                Resend invite
              </Button>
            )}
            <Button
              id="edit-profile"
              onClick={this.handleEdit}
              style={styles.editButton}
              variant="primary"
              icon={<Icon customIcon={IconType.Pencil} />}
              disabledByPermission={!hasRolePermission}
              data-testid="edit-profile-button"
            >
              Edit profile
            </Button>
          </StyledHeaderContainer>
        </PageHeader>
        <div data-testid="team-member-profile-content">
          {this.list(hasRolePermission)}
        </div>
      </StyledSContainer>
    )
  }
}

const isDesktop = isBrowser || isTablet

const styles = {
  editButton: {
    color: 'white',
    textShadow: 'none'
  },
  image: {
    position: 'relative',
    zIndex: 1,
    marginBottom: '-60px',
    marginTop: 30
  },
  infoContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  inviteButton: {
    color: colors.black,
    textShadow: 'none',
    marginRight: 15
  },
  mainContainer: {
    background: 'white',
    borderRadius: 15,
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: 24,
    padding: '16px 24px',
    marginBottom: 24
  }
}

TeamMemberProfileScreen.propTypes = {
  features: PropTypes.object.isRequired,
  profile: PropTypes.object,
  role: PropTypes.string,
  resendInvite: PropTypes.func,
  fetchCarerSkillOptions: PropTypes.func,
  fetchCarerSkills: PropTypes.func,
  fetchCarers: PropTypes.func,
  router: PropTypes.object,
  isDeviceAccessControl: PropTypes.bool,
  skills: PropTypes.array,
  homes: PropTypes.array
}

const mapStateToProps = (state, props) => ({
  features: state.global.home,
  profile: find(state.data.carers, { id: props.router.params.id }),
  role: state.global.user.role,
  residents: state.data.residents,
  homes: state.data.homes,
  isDeviceAccessControl: state.global.home.deviceAccessControl,
  skills: getCarerSkills(state, props),
  hasAccess: (roles, abilities) =>
    Selectors.checkRoleAccess(state, { roles, abilities }),
  gpConnectEnabled: !!state.global.home.odsCode && !!state.global.home.asidCode
})

const mapDispatchToProps = (dispatch) => ({
  fetchCarers: () => dispatch(Operations.loadList('carers', null, false)),
  fetchHomes: () => dispatch(Operations.loadList('homes')),
  resendInvite: (userId, cb) => dispatch(Operations.resendInvite(userId, cb)),
  fetchCarerSkills: (id) =>
    dispatch(
      Operations.loadList('skills', '', null, `/api/list/carers/${id}/skills`)
    ),
  fetchCarerSkillOptions: () =>
    dispatch(
      Operations.loadList('skill-need-templates/categories', { skills: 1 })
    )
})

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(TeamMemberProfileScreen)
)
